import moment from "moment";
import React from "react";
import Calendar, { DateUtils } from "react-day-picker";
import MomentLocaleUtils from "react-day-picker/moment";
import { useSelector } from "react-redux";

import { AppState } from "shared/data/reducers";
import { countriesWithUSFormat, monthYear } from "shared/lib/datetime";
import { formatMoney } from "shared/lib/functions";
import { priceFormatter } from "shared/lib/priceFormatter";
import { FlightDirection } from "shared/types";

import Img from "@components/FileServer/Img";
import FormattedMessage from "@components/UI/FormattedMessage";

type Props = {
  firstAvailableDate: Date;
  flights: {
    departure: string;
    price: number;
    totalPrice: number;
    currency: string;
    isLowestPrice: boolean;
  }[];
  flightDirection: FlightDirection;
  onDayClick: (day: Date, flightDirection: FlightDirection) => void;
  selectedDays: { from: Date; to: Date };
  locale: string;
  id: string;
};

const formatPrice = (price, currency, currentLanguage) => {
  if (!price) {
    return price;
  }

  const currentFormatOption = priceFormatter({});
  const money = formatMoney(
    price,
    currentFormatOption.decimalScale,
    currentFormatOption.decimalSeparator,
    currentFormatOption.thousandSeparator
  );

  const suffix = currency === "CZK" && currentLanguage === "cs" ? " Kč" : ",-";

  return `${money}${suffix}`;
};

function LowCostSearchCalendar(props: Props) {
  const defaultCountry = useSelector(
    (state: AppState) =>
      state.storage.frontendSettings?.dealerFrontendSettings?.defaultCountry ??
      ""
  );

  const { currentLanguage } = useSelector(
    (state: AppState) => state.requestorConfig
  );

  const modifiers = {
    availableDates: props.flights.map((flight) => new Date(flight.departure)),
  };

  const modifiersStyles = {
    availableDates: {
      fontWeight: "bold",
    },
  };

  const firstDayOfWeek = countriesWithUSFormat.includes(
    defaultCountry.toLowerCase()
  )
    ? 0
    : 1;

  const getFlightPerDay = (day) => {
    return props.flights.find(({ departure }) => {
      return DateUtils.isSameDay(day, new Date(departure));
    });
  };

  const isDayDisabled = (day, disabledDays) => {
    return !disabledDays.some((disabledDay) =>
      DateUtils.isSameDay(day, disabledDay)
    );
  };

  const getSelectedDays = () => {
    const days = [];
    if (props.flightDirection === "departure" && props.selectedDays.from) {
      days.push(props.selectedDays.from);
    }
    if (props.flightDirection === "return" && props.selectedDays.to) {
      days.push(props.selectedDays.to);
    }
    return days;
  };

  const getNavbarElement = () => <div className="DayPicker-NavBar" />;

  const getCaptionElement = (e) => (
    <div className="DayPicker-Caption">{monthYear(e.date, props.locale)}</div>
  );

  const renderDay = (day) => {
    const flight = getFlightPerDay(day);

    return (
      <div
        className={`day-container  ${flight ? "with-price" : ""}`}
        data-test-id={moment(day).format("YYYY-MM-DD")}
      >
        <div className="top">
          <div className="day">{day.getDate()}</div>
          <div className="icon">
            {flight && flight.isLowestPrice && (
              <Img src="/static/images/low-cost-lowest-price.svg" />
            )}
          </div>
        </div>
        <div className="bottom">
          <div className="price">
            {flight
              ? formatPrice(flight.totalPrice, flight.currency, currentLanguage)
              : ""}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="low-cost-search-calendar-container"
      data-test-id={moment(props.firstAvailableDate).format("YYYY-MM")}
    >
      {props.flightDirection === "departure" && (
        <div className="direction">
          <img
            alt="icon"
            className="direction-icon"
            src="/static/images/ico-plane-departure.svg"
          />
          <FormattedMessage id="LowCostSearch.wayThere" />
        </div>
      )}
      {props.flightDirection === "return" && (
        <div className="direction">
          <img
            alt="icon"
            className="direction-icon"
            src="/static/images/ico-plane-arrivals.svg"
          />
          <FormattedMessage id="LowCostSearch.wayBack" />
        </div>
      )}
      <Calendar
        className="DayPicker--lowCostSearch"
        numberOfMonths={1}
        firstDayOfWeek={firstDayOfWeek}
        month={props.firstAvailableDate}
        localeUtils={MomentLocaleUtils}
        locale={props.locale}
        modifiers={modifiers}
        modifiersStyles={modifiersStyles}
        disabledDays={(day) => isDayDisabled(day, modifiers.availableDates)}
        selectedDays={getSelectedDays()}
        onDayClick={(day) => props.onDayClick(day, props.flightDirection)}
        navbarElement={(e) => getNavbarElement(e)}
        captionElement={(e) => getCaptionElement(e)}
        renderDay={renderDay}
      />
    </div>
  );
}

export default LowCostSearchCalendar;
